import { useState } from 'react';
import { Pencil, RotateCcw } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  IconButton,
  PropertyList,
  PropertyRow,
  Skeleton,
} from '@/components/ui';
import { FieldValue } from './FieldValue';
import { FieldEditorForm } from './FieldEditor';
import {
  fieldSchemaError,
  type FieldDefinition,
  type FieldState,
  type TypedFieldValue,
} from './typedFieldData';
import type { FieldActions, FieldRegistry, FieldCommit, SecretAccess } from './fieldRegistry';

export function TypedField({
  definition,
  state,
  onCommit,
  onRetry,
  registry = {},
  secret,
  editable = true,
  valueAlignment = 'baseline',
  ...actions
}: FieldActions & {
  definition: FieldDefinition;
  state: FieldState;
  onCommit?: FieldCommit;
  onRetry?: () => void;
  registry?: FieldRegistry;
  secret?: SecretAccess;
  editable?: boolean;
  valueAlignment?: 'baseline' | 'first-line';
}) {
  const [open, setOpen] = useState(false),
    [session, setSession] = useState(0),
    [snapshot, setSnapshot] = useState<TypedFieldValue | null>(null);
  const canEdit =
    editable &&
    !definition.readOnly &&
    state.state === 'ready' &&
    (definition.type === 'secret' ? !!secret?.change : !!onCommit);
  const changeOpen = (next: boolean) => {
    if (next && state.state === 'ready') {
      setSnapshot(state.value);
      setSession((n) => n + 1);
    }
    setOpen(next);
  };
  return (
    <PropertyRow label={definition.label}>
      <div
        data-slot="typed-field"
        data-field-id={definition.id}
        data-field-type={definition.type}
        data-state={state.state}
        data-value-alignment={valueAlignment === 'first-line' ? 'first-line' : undefined}
        className={
          valueAlignment === 'first-line'
            ? 'group/field flex min-h-8 min-w-0 items-center gap-2'
            : 'group/field flex min-w-0 items-start gap-2'
        }
      >
        <div
          data-slot={valueAlignment === 'first-line' ? 'typed-field-value' : undefined}
          className={
            valueAlignment === 'first-line' ? 'flex min-w-0 flex-1 flex-col items-start' : 'min-w-0 flex-1'
          }
        >
          {state.state === 'loading' ? (
            <span role="status" aria-label={'Загрузка: ' + definition.label}>
              <Skeleton
                className={
                  definition.type === 'file'
                    ? 'h-16 w-full'
                    : definition.type === 'multiline'
                      ? 'h-12 w-full'
                      : 'h-4 w-24'
                }
              />
            </span>
          ) : state.state === 'error' ? (
            <div className="space-y-1">
              <p role="alert" className="text-xs text-danger-strong">
                {state.message}
              </p>
              {onRetry && (
                <Button type="button" size="sm" variant="ghost" leftIcon={<RotateCcw />} onClick={onRetry}>
                  Повторить
                </Button>
              )}
            </div>
          ) : (
            <FieldValue
              definition={definition}
              value={state.value}
              registry={registry}
              firstLineBaseline={valueAlignment === 'first-line'}
              secret={secret}
              {...actions}
            />
          )}
          {definition.help && <p className="mt-1 text-2xs text-fg-subtle">{definition.help}</p>}
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={changeOpen}>
            <DialogTrigger asChild>
              <IconButton
                type="button"
                variant="ghost"
                size="sm"
                aria-label={'Изменить: ' + definition.label}
                className="shrink-0 opacity-0 group-focus-within/field:opacity-100 group-hover/field:opacity-100 focus-visible:opacity-100"
              >
                <Pencil />
              </IconButton>
            </DialogTrigger>
            <DialogContent size="sm" className="max-h-full" data-typed-field-dialog={definition.id}>
              {snapshot && (definition.type !== 'secret' || open) && (
                <FieldEditorForm
                  key={session}
                  definition={definition}
                  initial={snapshot}
                  onCommit={onCommit ?? (() => undefined)}
                  registry={registry}
                  secret={secret}
                  onClose={() => setOpen(false)}
                />
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>
    </PropertyRow>
  );
}
export function TypedProperties({
  definitions,
  values,
  onCommit,
  onRetry,
  registry,
  secrets,
  editable,
  valueAlignment = 'baseline',
  ...actions
}: FieldActions & {
  definitions: readonly FieldDefinition[];
  values: Readonly<Record<string, FieldState>>;
  onCommit?: FieldCommit;
  onRetry?: (id: string) => void;
  registry?: FieldRegistry;
  secrets?: Readonly<Record<string, SecretAccess>>;
  editable?: boolean;
  valueAlignment?: 'baseline' | 'first-line';
}) {
  const error = fieldSchemaError(definitions);
  if (error)
    return (
      <p
        role="alert"
        className="rounded-md border border-danger-soft bg-danger-soft p-3 text-sm text-danger-strong"
      >
        Схема полей недоступна: {error}
      </p>
    );
  return (
    <div
      data-slot="typed-properties"
      className="@container/typed @sm/typed:[&_dd]:col-span-2 [&>dl>div]:grid-cols-1 [&>dl>div]:gap-1 @sm/typed:[&>dl>div]:grid-cols-3 @sm/typed:[&>dl>div]:gap-3"
    >
      <PropertyList>
        {definitions.map((definition) => (
          <TypedField
            key={definition.id}
            definition={definition}
            state={
              values[definition.id] ?? {
                state: 'ready',
                value: { type: definition.type, value: null } as TypedFieldValue,
              }
            }
            onCommit={onCommit}
            onRetry={onRetry ? () => onRetry(definition.id) : undefined}
            registry={registry}
            secret={secrets?.[definition.id]}
            editable={editable}
            valueAlignment={valueAlignment}
            {...actions}
          />
        ))}
      </PropertyList>
    </div>
  );
}
